import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Lock, 
  Zap,
  ArrowLeft,
  Fingerprint,
  Globe
} from 'lucide-react';
import { PortalCard, usePortalEntrance } from './PortalEntrance';

/**
 * Portal Selection Page
 * Dramatic entrance to each portal with immersive animations
 */
export const PortalSelector = () => {
  const { enterPortal, PortalEntranceComponent } = usePortalEntrance();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#0c1222] to-slate-900 relative overflow-hidden">
      {/* Portal Entrance Animation (renders when active) */}
      <PortalEntranceComponent />
      
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
      </div>
      
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 30 }).map((_, i) => (
          <motion.div
            key={i}
            initial={{ 
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: 0.3
            }}
            animate={{ 
              y: [null, Math.random() * -200, null],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 5 + Math.random() * 5,
              repeat: Infinity,
              delay: Math.random() * 5
            }}
            className="absolute w-1 h-1 rounded-full bg-neon-cyan"
          />
        ))}
      </div>
      
      {/* Header */}
      <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Link 
            to="/"
            className="inline-flex items-center text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
        
        {/* Title Section */}
        <div className="text-center py-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center space-x-3 mb-6"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-neon-cyan to-neon-purple flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-heading font-bold text-white">OVA Xchange</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl font-heading font-bold text-white mb-4"
          >
            Select Your Portal
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-slate-400 max-w-2xl mx-auto mb-8"
          >
            Enter the credential marketplace. Each portal is secured with 
            256-bit encryption and biometric verification.
          </motion.p>
          
          {/* Security badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex items-center justify-center space-x-6 text-sm text-slate-500"
          >
            <div className="flex items-center space-x-2">
              <Lock className="w-4 h-4 text-neon-cyan" />
              <span>End-to-End Encrypted</span>
            </div>
            <div className="flex items-center space-x-2">
              <Fingerprint className="w-4 h-4 text-neon-purple" />
              <span>Biometric Secured</span>
            </div>
            <div className="flex items-center space-x-2">
              <Globe className="w-4 h-4 text-neon-green" />
              <span>Distributed Ledger</span>
            </div>
          </motion.div>
        </div>
        
        {/* Portal Cards Grid */}
        <div className="max-w-6xl mx-auto px-4 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <PortalCard 
                portal="driver" 
                onClick={() => enterPortal('driver')}
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <PortalCard 
                portal="provider" 
                onClick={() => enterPortal('provider')}
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
            >
              <PortalCard 
                portal="broker" 
                onClick={() => enterPortal('broker')}
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 }}
            >
              <PortalCard 
                portal="patient" 
                onClick={() => enterPortal('patient')}
              />
            </motion.div>
          </div>
        </div>
        
        {/* Bottom info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center pb-12"
        >
          <p className="text-slate-600 text-sm">
            Don't have an account? 
            <Link to="/partner-application" className="text-neon-cyan ml-2 hover:underline">
              Apply for access
            </Link>
          </p>
        </motion.div>
      </div>
      
      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-neon-cyan/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-neon-purple/5 rounded-full blur-3xl" />
    </div>
  );
};

export default PortalSelector;
